import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import Loader from '../../common/loader/Loader'
import MemberEnrollmentCard from './MemberEnrollmentCard'
import { requestGetEnrollments } from '../../../actions/enrollmentsBillingActions'
import { deserialize } from '../../../utility/serialization'

import { ReactComponent as GolferIcon } from '../../../assets/images/common/icons/golfer-half-icon.svg'
import { ReactComponent as PlusIcon } from '../../../assets/images/common/icons/circle-plus-icon.svg'

import classes from './MemberEnrollmentsTab.module.scss'

// Every enrollment for one member, newest first, with its billing state. Reuses the Enrollments &
// Billing list endpoint scoped by user_id rather than a parallel endpoint, so the cards show the
// same package, amount and billing status the billing page does.
const MemberEnrollmentsTab = ({ student, onSelect, onCreateEnrollment }) => {
  const { t } = useTranslation()
  const communityId = useSelector(state => state.user.current_user.communities[0].id)
  const currency = useSelector(state => state.user.current_user.communities[0].currency)

  const [enrollments, setEnrollments] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const controller = new AbortController()

    requestGetEnrollments({
      communityId,
      perPage: 100,
      sortField: 'last_update',
      sortValue: 'desc',
      filters: { userId: student.id },
      signal: controller.signal,
    })
      .then(({ data }) => setEnrollments(deserialize(data).enrollments || []))
      .catch((error) => {
        if (error.name !== 'CanceledError') { setFailed(true) }
      })

    return () => controller.abort()
  }, [communityId, student.id])

  if (failed) {
    return <p className={classes.error}>{t('members.enrollmentsTab.loadError')}</p>
  }

  if (enrollments === null) {
    return <Loader />
  }

  // Enrolling the member in another program is reachable whether or not they already have one, so
  // the button sits below the list as well as in the empty state.
  const createButton = (
    <button type='button' className={classes.createButton} onClick={onCreateEnrollment}>
      <PlusIcon className={classes.createIcon} />
      {t('members.enrollmentsTab.createEnrollment')}
    </button>
  )

  if (enrollments.length === 0) {
    return (
      <div className={classes.emptyState}>
        <GolferIcon className={classes.emptyIcon} />
        <h6 className={classes.emptyTitle}>{t('members.enrollmentsTab.noEnrollments')}</h6>
        <p className={classes.emptyText}>{t('members.enrollmentsTab.noEnrollmentsDescription')}</p>
        {createButton}
      </div>
    )
  }

  return (
    <div className={classes.list}>
      {createButton}
      {enrollments.map(enrollment => (
        <MemberEnrollmentCard
          key={enrollment.id}
          enrollment={enrollment}
          currency={currency}
          onSelect={onSelect}
        />
      ))}
    </div>
  )
}

MemberEnrollmentsTab.propTypes = {
  student: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  onCreateEnrollment: PropTypes.func.isRequired,
}

export default MemberEnrollmentsTab
